import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBlocNext } from "@blac/react";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import ChatComposer from "./ChatComposer";
import ChatDetailHeader from "./NavButton";
import {
  LeapChatContainer,
  LeapChatViewport,
  EmptyChatHero,
  HeroLogo,
  HeroHeading,
  ComposerCardArea
} from "./leapChat.styles";
import chatLogo from "src/ui/assets/logos/9am_chat_logo.svg";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import type { LeapChatAttachment } from "./state/types";
import { ConversationService } from "./state/ConversationService";

const NewConversationView: FC = () => {
  const [{ "auth.user.firstname": firstName, "auth.user.nickname": nickName }] =
    useBlocNext(UserPreferencesCubit);
  const navigate = useNavigate();
  const serviceRef = useRef(ConversationService.getInstance());

  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState<LeapChatAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      serviceRef.current.revokePreviewUrls(attachments);
    };
    // Only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddFile = useCallback((file: File) => {
    const service = serviceRef.current;
    const attachment = service.createAttachment(file);
    setAttachments((prev) => [...prev, attachment]);

    void service
      .uploadAttachmentFile(file)
      .then((fileId) => {
        setAttachments((prev) =>
          prev.map((a) =>
            a.key === attachment.key
              ? { ...a, fileId, status: fileId ? "done" : "error" }
              : a
          )
        );
      })
      .catch(() => {
        setAttachments((prev) =>
          prev.map((a) =>
            a.key === attachment.key ? { ...a, status: "error" } : a
          )
        );
      });
  }, []);

  const handleRemoveAttachment = useCallback((key: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.key === key);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((a) => a.key !== key);
    });
  }, []);

  const handleSend = useCallback(async () => {
    const service = serviceRef.current;
    const text = inputValue.trim();
    const doneCount = attachments.filter((a) => a.status === "done").length;
    if ((!text && doneCount === 0) || isSubmitting) {
      return;
    }

    const { fileIds, files } = service.extractAttachmentPayload(attachments);

    setIsSubmitting(true);
    const conversationId = await service.createConversation();
    if (conversationId) {
      service.setPendingFirstMessage(text || "", fileIds, files);
      service.revokePreviewUrls(attachments);
      navigate(`/app/chat/conversation/${conversationId}`, { replace: true });
    }
    setIsSubmitting(false);
  }, [inputValue, attachments, isSubmitting, navigate]);

  return (
    <LeapChatContainer>
      <LeapChatViewport>
        <ChatDetailHeader showArchive />
        <EmptyChatHero>
          <HeroLogo src={chatLogo} alt="9am" />
          <HeroHeading>
            {translate("leapChat.greeting" as TranslationKey, {
              name: nickName ?? firstName
            })}
          </HeroHeading>
        </EmptyChatHero>
        <ComposerCardArea>
          <ChatComposer
            onSend={() => void handleSend()}
            inputValue={inputValue}
            onInputChange={setInputValue}
            isRunning={isSubmitting}
            attachments={attachments}
            onAddFile={handleAddFile}
            onRemoveAttachment={handleRemoveAttachment}
            placeholder={translate(
              "leapChat.placeholder.askAnything" as TranslationKey
            )}
          />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default NewConversationView;
